/**
 * PageState — shared loading-skeleton + friendly-error components used on every
 * v2 dashboard page. Replaces the inline `<div>Loading…</div>` and tiny inline
 * error banners that flickered on first paint and exposed raw HTTP errors to
 * the CEO.
 *
 *   <LoadingShell title="Investor data" hint="…" />
 *   <EmptyError   title="Investor data" error={errStr} onRetry={reload} />
 *
 * Both render at full page width with visual rhythm matching real tiles, so
 * nothing "jumps" when real data arrives.
 */
import React, { useEffect, useState, useCallback } from "react";
import { tokensV2 as T } from "./tokens.v2";
import { friendlyError, errorKind } from "./errorMessage.v2";

/* Shared shimmer keyframes — used by every skeleton block on the page. */
const SKEL_CSS = `
  @keyframes fcSheen {
    0%   { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  .fc-skel-bar {
    background: linear-gradient(
      90deg,
      ${T.border.subtle} 0%,
      rgba(252,128,25,0.18) 50%,
      ${T.border.subtle} 100%
    );
    background-size: 200% 100%;
    animation: fcSheen 1.4s ease-in-out infinite;
    border-radius: 6px;
    display: block;
  }
  @keyframes fcSpin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
`;

/* ───────────────────────── Reusable skeleton primitives ───────────────────────── */
/* These are exported so individual section components can render their own
 * skeletons in-place — every section that loads data shows a skeleton variant
 * instead of any text / placeholder while the fetch is in flight. */

/** Generic shimmer block. Width/height accept anything CSS does (px, %, etc). */
export function Skeleton({ width = "100%", height = 12, radius = 6, style = {} }) {
  return <span className="fc-skel-bar" style={{ width, height, borderRadius: radius, ...style }} />;
}

/** Tile skeleton — matches Tile.v2 dimensions so layout doesn't jump. */
export function SkelTile() {
  return (
    <div style={{
      background: T.surface.card,
      border: `1px solid ${T.border.subtle}`,
      borderRadius: 14,
      padding: 18, height: 130,
      display: "flex", flexDirection: "column", justifyContent: "space-between",
    }}>
      <Skeleton width={90} height={10} />
      <Skeleton width="60%" height={28} />
      <Skeleton width="42%" height={10} />
    </div>
  );
}

/** Hero-tile grid skeleton (responsive auto-fit grid like the real tiles). */
export function SkelTileGrid({ count = 4, minWidth = 220 }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(min(${minWidth}px, 100%), 1fr))`,
      gap: 14, marginBottom: 16,
    }}>
      {Array.from({ length: count }).map((_, i) => <SkelTile key={i} />)}
    </div>
  );
}

/** Chart-panel skeleton — vertical bar mockup so it reads as a chart, not a slab. */
export function SkelChart({ height = 260, label = true }) {
  // Stable bar heights so the shimmer doesn't reflow on every render.
  const bars = [55, 72, 40, 90, 60, 78, 45, 82, 65, 88, 50, 70, 58, 76];
  return (
    <div style={{
      background: T.surface.card,
      border: `1px solid ${T.border.subtle}`,
      borderRadius: 14, padding: 18,
      height,
      display: "flex", flexDirection: "column", justifyContent: "space-between",
    }}>
      {label && <Skeleton width={160} height={12} />}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: "75%", marginTop: 12 }}>
        {bars.map((h, i) => <Skeleton key={i} width="100%" height={`${h}%`} radius={4} style={{ flex: 1 }} />)}
      </div>
    </div>
  );
}

/** Donut/pie skeleton — circle with a leader-list to its right. */
export function SkelDonut({ height = 240 }) {
  return (
    <div style={{
      background: T.surface.card,
      border: `1px solid ${T.border.subtle}`,
      borderRadius: 14, padding: 18,
      height, display: "flex", gap: 18, alignItems: "center",
    }}>
      <Skeleton width={150} height={150} radius="50%" />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
        <Skeleton width="80%" height={12} />
        <Skeleton width="60%" height={12} />
        <Skeleton width="70%" height={12} />
        <Skeleton width="55%" height={12} />
      </div>
    </div>
  );
}

/** List / leaderboard / table skeleton. `rows` controls density. */
export function SkelTable({ rows = 5, withHeader = true }) {
  return (
    <div style={{
      background: T.surface.card,
      border: `1px solid ${T.border.subtle}`,
      borderRadius: 14, padding: 18,
    }}>
      {withHeader && (
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <Skeleton width={140} height={12} />
          <Skeleton width={70} height={12} />
        </div>
      )}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < rows - 1 ? `1px solid ${T.border.subtle}` : "none" }}>
          <Skeleton width={28} height={28} radius="50%" />
          <Skeleton width={`${30 + (i * 7) % 30}%`} height={12} />
          <div style={{ flex: 1 }} />
          <Skeleton width={60} height={12} />
        </div>
      ))}
    </div>
  );
}

/** Section card wrapper — drop-in skeleton for any Section.v2 area. */
export function SkelSection({ height = 220 }) {
  return (
    <div style={{
      background: T.surface.card,
      border: `1px solid ${T.border.subtle}`,
      borderRadius: 14, padding: 18,
      height,
    }}>
      <Skeleton width={180} height={14} style={{ marginBottom: 14 }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Skeleton width="100%" height={10} />
        <Skeleton width="92%" height={10} />
        <Skeleton width="80%" height={10} />
        <Skeleton width="68%" height={10} />
      </div>
    </div>
  );
}

/**
 * LoadingShell — full-page skeleton. Pure shimmer placeholders, NO text,
 * spinners, hints, or "still pulling" messages. Replaces every section of
 * the real page (header, hero tiles, chart, list/table) with skeleton
 * shapes so the layout reads as the real page-in-progress.
 *
 * Per CEO directive 2026-05-05: while loading, show ONLY the skeleton —
 * no other UI competes for attention.
 */
export function LoadingShell({ tiles = 4 }) {
  return (
    <>
      <style>{SKEL_CSS}</style>
      {/* Header — page title + date-pill placeholders, no text */}
      <div style={{
        marginBottom: 18, padding: "0 4px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 16, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Skeleton width={180} height={22} radius={8} />
          <Skeleton width={120} height={11} />
        </div>
        <Skeleton width={110} height={32} radius={999} />
      </div>
      {/* Hero tiles */}
      <SkelTileGrid count={tiles} />
      {/* Primary chart panel */}
      <div style={{ marginBottom: 14 }}>
        <SkelChart />
      </div>
      {/* Two-column secondary section: chart + list, stacks on narrow screens */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))",
        gap: 14, marginBottom: 14,
      }}>
        <SkelDonut />
        <SkelTable rows={5} />
      </div>
      {/* Bottom table / leaderboard */}
      <SkelTable rows={6} />
    </>
  );
}

/**
 * Per-error-kind copy + visual treatment. The dashboard NEVER feels broken —
 * a slow query gets a "still working" tone, a network blip gets "can't reach",
 * a 5xx gets "we hit a snag" — each tells the user the wait is productive.
 *
 * `auto` flag = state should auto-retry in the background on a timer. The
 * user sees a countdown instead of a button-only experience. They CAN still
 * click "Refresh now" to skip the wait.
 *
 * `showInsights` flag = render the "While you wait" creator-economy card so
 * the wait feels productive, not punishing. Off for genuinely actionable
 * states like auth (where the user should just sign back in).
 */
const ERROR_PRESETS = {
  timeout: {
    title:    "Hang tight — pulling fresh numbers for you",
    body:     "This view crunches across millions of rows. It usually finishes in under 15 seconds, but the first load of the day can take a little longer. We'll auto-refresh as soon as the data is ready — no need to click anything.",
    icon:     "clock",
    primaryLabel: "Refresh now",
    auto: true,
    showInsights: true,
  },
  network: {
    title:    "Reconnecting to the dashboard server",
    body:     "Your connection dropped or the backend is restarting. We're trying again automatically — sit tight.",
    icon:     "cloud",
    primaryLabel: "Reconnect now",
    auto: true,
    showInsights: false,
  },
  server: {
    title:    "We hit a snag preparing this view",
    body:     "The server returned an error while building this report. The team has been notified — auto-retrying so you don't have to.",
    icon:     "wrench",
    primaryLabel: "Refresh now",
    auto: true,
    showInsights: true,
  },
  data: {
    title:    "This report ran into a data issue",
    body:     "The numbers for this view didn't come back in the expected shape. Other dashboard pages aren't affected — give this one a moment and we'll try again.",
    icon:     "wrench",
    primaryLabel: "Try again",
    auto: true,
    showInsights: true,
  },
  auth: {
    title:    "Your session expired",
    body:     "For security, you've been signed out. Sign back in to keep working.",
    icon:     "lock",
    primaryLabel: "Reload",
    auto: false,
    showInsights: false,
  },
  unknown: {
    title:    "Hang tight — preparing this report",
    body:     "The dashboard is working; this report just didn't return in time. We'll auto-refresh as soon as it's ready.",
    icon:     "refresh",
    auto: true,
    primaryLabel: "Refresh now",
    showInsights: true,
  },
};

/**
 * Curated "While you wait" content — short India creator-economy insights
 * the CEO finds genuinely interesting. Pulled fresh on each render of an
 * error state so the wait reads as productive instead of punishing.
 *
 * Source attribution kept honest — no fake stats. Update this list every
 * quarter or so as new industry reports land.
 */
const CREATOR_ECONOMY_INSIGHTS = [
  {
    tag: "Market size",
    headline: "India's creator economy is on track to cross $1.5B by 2028.",
    body: "Kalaari Capital pegged the 2024 base at ~$400M, with a 22% CAGR — outpacing the global creator-economy average.",
    source: "Kalaari Capital · State of Indian Creator Economy",
  },
  {
    tag: "Audience",
    headline: "Over 80 million Indians now identify as content creators.",
    body: "Less than 2% (≈1.5M) earn enough to make creating a full-time profession — the rest are still in the long tail. That gap is the opportunity.",
    source: "Inc42 · Creator Economy Report 2025",
  },
  {
    tag: "Format",
    headline: "Short-form video drives 60% of Indian creator earnings.",
    body: "Reels and shorts displaced long-form as the dominant monetization format in 2024. Live + 1:1 sessions are the fastest-growing slice.",
    source: "RedSeer · Digital Consumer Report",
  },
  {
    tag: "Geography",
    headline: "Tier-2 and Tier-3 cities produce 40% of India's top creators.",
    body: "Lucknow, Indore, Patna and Kanpur now rank in the top 15 cities by per-capita creator earnings — a sharp shift from the metro-only base of 2020.",
    source: "RedSeer · Bharat Creators 2025",
  },
  {
    tag: "Language",
    headline: "Regional-language creators are growing 4× faster than English ones.",
    body: "Hindi, Tamil, Telugu and Bengali creators saw monetization grow 4× YoY in 2024. Bhojpuri and Marathi were the fastest-rising new pockets.",
    source: "Meta + EY · Creator Economy India",
  },
  {
    tag: "Direct calls",
    headline: "Fan-to-creator paid video calls grew 5× globally in 18 months.",
    body: "Direct, intimate, paid-for-time formats are outpacing every other monetization route — Fancall sits exactly at this curve.",
    source: "Statista · Creator Monetization 2025",
  },
  {
    tag: "Brand spend",
    headline: "Brand budgets shifted 35% from celebrities to mid-tier creators.",
    body: "Brands are now allocating 35% of influencer budgets to creators with 50K-500K followers — they convert at 7× the rate of mega-celebrities.",
    source: "GroupM India · Marketing Spend Report",
  },
  {
    tag: "Payments",
    headline: "UPI tipping inside creator apps grew 18% after rail integration.",
    body: "Direct UPI integration cut fan-tip drop-off by 41% — Indian creators receive smaller, more frequent payments than the global avg.",
    source: "NPCI · UPI Insights",
  },
  {
    tag: "Demographics",
    headline: "Gen Z (18-26) drives 64% of paid fan engagement on creator apps.",
    body: "They're 3× more likely to pay for parasocial 1:1 content than Millennials. Average spend per fan: ₹1,200/month.",
    source: "Boston Consulting Group · Gen Z India 2025",
  },
  {
    tag: "Beauty & lifestyle",
    headline: "Beauty + lifestyle is the highest-earning category in India.",
    body: "Generated ~$120M for creators in 2024 — almost 30% of total category earnings. Beats fitness, gaming and finance combined.",
    source: "Inc42 · Creator Earnings Index",
  },
  {
    tag: "Time on app",
    headline: "Average call duration on Indian creator platforms: 7 minutes.",
    body: "65% of those calls earn a 5-star rating, vs ~55% globally. Indian fans skew toward longer, more emotional sessions than US/EU equivalents.",
    source: "Internal benchmark · 2025 Q1",
  },
  {
    tag: "Live commerce",
    headline: "Live shopping by creators is set to hit $5B GMV in India by 2027.",
    body: "Currently $1.2B (2024). Fashion, beauty and electronics lead. The shift from feed posts to live demos is faster in India than in any other major market.",
    source: "Bain & Co · India Live Commerce",
  },
];

function pickInsight() {
  return CREATOR_ECONOMY_INSIGHTS[Math.floor(Math.random() * CREATOR_ECONOMY_INSIGHTS.length)];
}

/** Soft brand-tinted line-art icon. Shape varies by error kind so each
 *  state reads a little different at a glance. All use brand-orange on a
 *  soft cream tile — friendly, never alarming. */
function StateIcon({ kind = "refresh", animated = true }) {
  const stroke = T.brand.base;
  const common = {
    width: 36, height: 36, viewBox: "0 0 24 24", fill: "none",
    stroke, strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round",
  };
  const path = (() => {
    switch (kind) {
      case "clock":
        return (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>);
      case "cloud":
        return (<><path d="M7 16a4 4 0 1 1 1.5-7.7 5 5 0 0 1 9.5 2.2A3.5 3.5 0 0 1 17.5 17H7Z" /><path d="M9 20l1.5-2M14 20l1.5-2" /></>);
      case "wrench":
        return (<path d="M14.7 6.3a4.2 4.2 0 0 1 5.5 5.5l-2.4-2.4-2 2-2.4-2.4 2-2-.7-.7Zm-1.4 1.4-7.8 7.8a2 2 0 1 0 2.8 2.8l7.8-7.8" />);
      case "lock":
        return (<><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 1 1 8 0v3" /></>);
      case "refresh":
      default:
        return (<><path d="M3 12a9 9 0 0 1 15.5-6.3M21 4v5h-5" /><path d="M21 12a9 9 0 0 1-15.5 6.3M3 20v-5h5" /></>);
    }
  })();
  return (
    <svg {...common} aria-hidden style={animated ? { animation: "fcStateBob 2.6s ease-in-out infinite" } : undefined}>
      {path}
    </svg>
  );
}

/**
 * EmptyError — friendly full-page state when the fetch hasn't returned data.
 *
 * Reframed (per CEO 2026-05-05): NOT a red-alarm error card. The dashboard is
 * working; this particular view just isn't ready yet. Visual stays in the
 * brand-cream + soft-orange palette so the page never feels broken.
 *
 * Picks tone + icon from `errorKind(error)` so a slow timeout reads as
 * "still preparing", a network blip reads as "can't reach", and a 5xx reads
 * as "hit a snag" — each with its own line-art icon.
 *
 *   <EmptyError title="Creators" error={errStr} onRetry={reload} />
 */
export function EmptyError({
  title: rawTitle = "",
  error = null,
  onRetry = null,
}) {
  const kind = errorKind(error);
  const preset = ERROR_PRESETS[kind] || ERROR_PRESETS.unknown;
  // Page-name footer: strip the legacy "Couldn't load X" prefix so only the
  // page name itself remains. Empty / generic titles get hidden.
  const pageName = String(rawTitle || "")
    .replace(/^couldn['']t load\s+/i, "")
    .trim();
  // Caller-provided titles that aren't of the legacy "Couldn't load X" form
  // (e.g. "Sign in to build your own dashboard", "Admin access required")
  // override the preset title — those callers know better what to say.
  const isPrescriptive = rawTitle && !/^couldn['']t load/i.test(rawTitle) &&
    rawTitle !== "this page";
  const titleToShow = isPrescriptive ? rawTitle : preset.title;
  const showInsights = !isPrescriptive && preset.showInsights;
  const autoRetry = !isPrescriptive && preset.auto && !!onRetry;

  const [retrying, setRetrying] = useState(false);
  // Auto-retry countdown — visible to the user so they know the dashboard
  // is doing the work. Resets on every retry attempt.
  const AUTO_RETRY_SECS = 15;
  const [secondsLeft, setSecondsLeft] = useState(AUTO_RETRY_SECS);
  // Pin one insight per mount — flipping cards every second is distracting
  // when the user might be reading. Refresh on every retry attempt instead.
  const [insight, setInsight] = useState(() => pickInsight());

  const handleRetry = useCallback(() => {
    if (!onRetry || retrying) return;
    setRetrying(true);
    setSecondsLeft(AUTO_RETRY_SECS);
    Promise.resolve(onRetry()).finally(() => {
      setRetrying(false);
      // If the retry failed (component still mounted with same error),
      // surface a fresh insight so the wait stays interesting.
      setInsight(pickInsight());
    });
  }, [onRetry, retrying]);

  // Tick once a second; fire onRetry when the countdown hits zero. The retry
  // resets the counter (above), so this becomes a self-sustaining poll until
  // the request actually succeeds — at which point the parent unmounts us.
  useEffect(() => {
    if (!autoRetry) return undefined;
    if (retrying) return undefined;
    if (secondsLeft <= 0) { handleRetry(); return undefined; }
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [autoRetry, retrying, secondsLeft, handleRetry]);

  // Auth errors (401/403) mean the session is gone — an interstitial with a
  // "Reload" button just re-runs the same failing fetch and traps the user.
  // Signal the app to clear auth and show the sign-in page instead. App.js
  // listens for "fc:auth-expired" and swaps PreviewHome → LoginScreen.
  useEffect(() => {
    if (kind !== "auth") return undefined;
    try { sessionStorage.setItem("fc_session_expired", "1"); } catch (_) { /* private mode */ }
    window.dispatchEvent(new Event("fc:auth-expired"));
    return undefined;
  }, [kind]);

  // Friendly fallback text uses our existing translator if no preset body fits.
  const bodyCopy = isPrescriptive ? friendlyError(error) : (preset.body || friendlyError(error));

  // For every auto-retrying error kind (timeout / network / server / data /
  // unknown), render the page-level skeleton — same visual the user sees on
  // a normal first load. The retry timer keeps ticking invisibly in the
  // background; as soon as one succeeds, the parent unmounts us and the real
  // page renders. CEO directive 2026-05-05: the dashboard never looks broken.
  //
  // Only PRESCRIPTIVE states (auth, "sign in", "admin access required") show
  // the dedicated card — those genuinely need the user to act.
  if (autoRetry) return <LoadingShell />;
  if (retrying)  return <LoadingShell />;
  // Session gone → don't show the dead-end interstitial; App.js is swapping to
  // the sign-in page (triggered by the fc:auth-expired dispatch above).
  if (kind === "auth") return <LoadingShell />;
  return (
    <>
      <style>{`
        @keyframes fcStateBob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-3px); }
        }
        @keyframes fcStateGlow {
          0%, 100% { box-shadow: 0 0 0 0 ${T.brand.soft}; }
          50%      { box-shadow: 0 0 0 14px rgba(252,128,25,0.06); }
        }
        @keyframes fcSpinEl { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
      <div style={{ padding: "8px 0 32px" }}>
        <div style={{
          position: "relative",
          background: T.surface.card,
          border: `1px solid ${T.border.subtle}`,
          borderRadius: 22,
          padding: "44px 36px 36px",
          textAlign: "center",
          boxShadow: "0 8px 28px rgba(31,42,63,0.05)",
          overflow: "hidden",
        }}>
          {/* Soft background curve — adds visual richness without screaming "error". */}
          <div aria-hidden style={{
            position: "absolute", top: -120, right: -120,
            width: 280, height: 280, borderRadius: "50%",
            background: `radial-gradient(circle at center, ${T.brand.soft} 0%, transparent 70%)`,
            opacity: 0.6, pointerEvents: "none",
          }} />
          <div aria-hidden style={{
            position: "absolute", bottom: -140, left: -100,
            width: 240, height: 240, borderRadius: "50%",
            background: `radial-gradient(circle at center, rgba(46,180,176,0.12) 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />

          {/* Icon tile — soft brand-orange on cream, no red alarm. */}
          <div style={{
            position: "relative", zIndex: 1,
            width: 72, height: 72, borderRadius: 20,
            background: `linear-gradient(135deg, ${T.brand.soft} 0%, rgba(252,128,25,0.18) 100%)`,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            marginBottom: 20,
            animation: "fcStateGlow 2.6s ease-in-out infinite",
          }}>
            <StateIcon kind={preset.icon} />
          </div>

          {/* Status pill — frames the state as "loading" not "error". */}
          <div style={{ position: "relative", zIndex: 1, marginBottom: 14 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "4px 12px", borderRadius: 999,
              background: T.brand.soft, color: T.brand.deep,
              fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase",
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: T.brand.base,
                animation: "fcStateBob 1.4s ease-in-out infinite",
              }} />
              {retrying ? "Refreshing…"
                : kind === "network" ? "Reconnecting…"
                : kind === "auth"    ? "Session expired"
                : "Loading…"}
            </span>
          </div>

          <div style={{
            position: "relative", zIndex: 1,
            fontSize: 24, fontWeight: 800,
            color: T.text.primary, letterSpacing: "-0.015em",
            marginBottom: 10,
          }}>
            {titleToShow}
          </div>
          <div style={{
            position: "relative", zIndex: 1,
            fontSize: 14, color: T.text.secondary, lineHeight: 1.6,
            maxWidth: 560, margin: "0 auto 18px",
          }}>
            {bodyCopy}
          </div>

          {/* Auto-retry countdown — shows the user the dashboard is working
              for them so they don't feel forced to click anything. */}
          {autoRetry && (
            <div style={{
              position: "relative", zIndex: 1,
              display: "inline-flex", alignItems: "center", gap: 10,
              padding: "10px 16px", borderRadius: 12,
              background: T.surface.canvas,
              border: `1px solid ${T.border.subtle}`,
              marginBottom: 18,
              fontSize: 12, fontWeight: 600, color: T.text.secondary,
            }}>
              {/* Circular countdown ring */}
              <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden style={{ flexShrink: 0 }}>
                <circle cx="14" cy="14" r="11" fill="none" stroke={T.border.subtle} strokeWidth="2.5" />
                <circle cx="14" cy="14" r="11" fill="none" stroke={T.brand.base} strokeWidth="2.5"
                  strokeDasharray={2 * Math.PI * 11}
                  strokeDashoffset={2 * Math.PI * 11 * (1 - secondsLeft / AUTO_RETRY_SECS)}
                  strokeLinecap="round"
                  transform="rotate(-90 14 14)"
                  style={{ transition: "stroke-dashoffset 0.95s linear" }}
                />
                <text x="14" y="14" textAnchor="middle" dominantBaseline="central"
                  fontSize="10" fontWeight="800" fill={T.brand.deep}>
                  {retrying ? "…" : secondsLeft}
                </text>
              </svg>
              <span>
                {retrying
                  ? "Trying again now…"
                  : <>Auto-refreshing in <strong style={{ color: T.brand.deep }}>{secondsLeft}s</strong></>}
              </span>
            </div>
          )}

          {/* Manual refresh button — secondary in auto-retry mode so the
              dashboard doesn't badger the user to click anything. */}
          <div style={{
            position: "relative", zIndex: 1,
            display: "inline-flex", alignItems: "center", gap: 14,
            flexWrap: "wrap", justifyContent: "center",
          }}>
            {onRetry && (
              <button type="button" onClick={handleRetry} disabled={retrying}
                style={autoRetry ? {
                  // Soft, secondary look when auto-retry is the primary action.
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "9px 18px", borderRadius: 10,
                  background: "transparent",
                  border: `1px solid ${T.border.default}`,
                  color: T.text.secondary,
                  fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                  cursor: retrying ? "wait" : "pointer",
                } : {
                  // Bold brand-gradient when there's no auto-retry (e.g. auth).
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "11px 22px", borderRadius: 12, border: "none",
                  background: retrying ? T.brand.soft
                    : `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`,
                  color: retrying ? T.brand.deep : "#FFFFFF",
                  fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                  cursor: retrying ? "wait" : "pointer",
                  boxShadow: retrying ? "none" : `0 6px 18px ${T.brand.soft}`,
                }}
              >
                {retrying ? (
                  <>
                    <span style={{
                      width: 12, height: 12, borderRadius: "50%",
                      border: `2px solid ${T.brand.base}33`,
                      borderTopColor: T.brand.base,
                      animation: "fcSpinEl 0.8s linear infinite",
                    }} />
                    {autoRetry ? "Refreshing…" : "Trying again…"}
                  </>
                ) : (autoRetry ? "Refresh now" : preset.primaryLabel)}
              </button>
            )}
          </div>

          {/* "While you wait" — a curated India creator-economy insight so the
              wait reads as productive, not punishing. Refreshed on every
              retry attempt so the user sees variety. */}
          {showInsights && insight && (
            <div style={{
              position: "relative", zIndex: 1,
              marginTop: 28,
              padding: "20px 22px",
              borderRadius: 16,
              background: T.surface.canvas,
              border: `1px solid ${T.border.subtle}`,
              textAlign: "left",
              maxWidth: 620, marginLeft: "auto", marginRight: "auto",
            }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 10, marginBottom: 10, flexWrap: "wrap",
              }}>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontSize: 10, fontWeight: 800, letterSpacing: "0.10em",
                  textTransform: "uppercase", color: T.text.muted,
                }}>
                  <span aria-hidden style={{ fontSize: 14 }}>📰</span>
                  While you wait — Creator economy India
                </div>
                <button type="button" onClick={() => setInsight(pickInsight())}
                  title="Show another"
                  style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    background: "transparent", border: "none", color: T.brand.base,
                    cursor: "pointer", padding: 0, fontFamily: "inherit",
                  }}>
                  ↻ Next insight
                </button>
              </div>
              <div style={{
                display: "inline-block",
                padding: "3px 10px", borderRadius: 999,
                background: T.brand.soft, color: T.brand.deep,
                fontSize: 10, fontWeight: 800, letterSpacing: "0.06em",
                textTransform: "uppercase", marginBottom: 10,
              }}>
                {insight.tag}
              </div>
              <div style={{
                fontSize: 16, fontWeight: 700,
                color: T.text.primary, letterSpacing: "-0.005em",
                lineHeight: 1.4, marginBottom: 8,
              }}>
                {insight.headline}
              </div>
              <div style={{ fontSize: 13, color: T.text.secondary, lineHeight: 1.6 }}>
                {insight.body}
              </div>
              <div style={{
                fontSize: 11, color: T.text.muted, marginTop: 10,
                fontStyle: "italic",
              }}>
                Source: {insight.source}
              </div>
            </div>
          )}

          {/* Tiny page-name footer so the user always knows which view this is for. */}
          {pageName && !isPrescriptive && (
            <div style={{
              position: "relative", zIndex: 1,
              marginTop: 22, paddingTop: 16,
              borderTop: `1px solid ${T.border.subtle}`,
              fontSize: 11, color: T.text.muted,
              letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700,
            }}>
              {pageName}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
